import { withJsonErrorBody } from "@lib/apiError"
import { filenameFromContentDisposition } from "@lib/download"

import { apiClient, TRANSFER_TIMEOUT_MS } from "./client"

import type {
  Classroom,
  ClassroomMaterialGaps,
  ClassroomResults,
  MaterialGapFillResult,
} from "@/types"

/** Which slice of the kardex to export: one cut by number, or the whole course. */
export type GradesExportScope = number | "all"

export interface CreateClassroomPayload {
  // PRD-37 — replaces the old `subject: string` enum value with the FK id
  // of a school-scoped Subject row (see src/api/subjects.ts).
  subject_id: string
  // PRD-47 v2 — `education_level` left this payload: the backend stamps the
  // school's admin-set level and ignores anything a legacy client sends.
  // PRD-38 §FR-8 — the PROGRAM-WIDE period (semester 3 of the program, not of
  // the year). Validated server-side against the institution's
  // `periods_per_cycle` (422 period_number_out_of_range).
  period_number?: number
  section?: string
  // PRD-30 — optional Lucide icon name; omitted/blank applies the subject default.
  icon?: string
  // PRD-38 §FR-27/28 — `semester` (the school YEAR) and `grade` left this
  // payload with the form controls. The backend derives the school year from
  // `request.auth.semester` and leaves `grade` "". Both remain ACCEPTED by
  // `ClassroomIn` for API clients that still send them, so they stay here as
  // optional escape hatches rather than being deleted from the contract —
  // the app itself never sets either one.
  semester?: string
  grade?: string
}

// Unchanged by PRD-38 §FR-26: dropping the CREATE controls does not touch the
// update contract. `semester` / `grade` stay here because they mirror the
// backend's `ClassroomUpdate` (which still accepts them for historical rows);
// the app's only update surface, EditClassroomModal, sets `classroom_alias`
// and `icon` exclusively.
export interface UpdateClassroomPayload {
  // null clears the alias so effective_display_name falls back to canonical_label.
  classroom_alias?: string | null
  subject_id?: string
  semester?: string
  grade?: string
  section?: string
  // PRD-30 — null/empty resets icon to the subject default; mirrors classroom_alias tri-state.
  icon?: string | null
}

export const classroomsApi = {
  // PRD-45 §FR-2.3 — `includeArchived` omitted/false returns active-only
  // (the backend's own default); every existing call site (sidebar,
  // material-request picker, …) keeps calling `list()` with no args and
  // stays correct for free. Only the /salones "Archivados" tab passes
  // `{ includeArchived: true }`.
  list: (params?: { includeArchived?: boolean }) =>
    apiClient
      .get<Classroom[]>("/classrooms/", {
        params: params?.includeArchived ? { include_archived: true } : undefined,
      })
      .then((r) => r.data),

  get: (id: string) => apiClient.get<Classroom>(`/classrooms/${id}`).then((r) => r.data),

  create: (data: CreateClassroomPayload) =>
    apiClient.post<Classroom>("/classrooms/", data).then((r) => r.data),

  update: (id: string, data: UpdateClassroomPayload) =>
    apiClient.patch<Classroom>(`/classrooms/${id}`, data).then((r) => r.data),

  // PRD-45 — reversible declutter. Idempotent on the backend: archiving
  // an already-archived classroom (or restoring an already-active one)
  // is a 200 no-op, not a 409.
  archive: (id: string) =>
    apiClient.post<Classroom>(`/classrooms/${id}/archive`).then((r) => r.data),

  restore: (id: string) =>
    apiClient.post<Classroom>(`/classrooms/${id}/restore`).then((r) => r.data),

  // PRD-61 §FR-3.1 — per-cut and whole-course figures plus every sent,
  // gradeable material with the cut it counts for. Read-only.
  results: (id: string) =>
    apiClient.get<ClassroomResults>(`/classrooms/${id}/results`).then((r) => r.data),

  // PRD-61 §FR-4 — the kardex CSV (UTF-8 with BOM) for one cut or the whole
  // course. `filename` is null when the Content-Disposition header is absent
  // or not exposed cross-origin; the caller names the file then.
  gradesExport: async (id: string, scope: GradesExportScope) => {
    try {
      const res = await apiClient.get<Blob>(`/classrooms/${id}/grades-export`, {
        params: { cut: scope },
        responseType: "blob",
        timeout: TRANSFER_TIMEOUT_MS,
      })
      return {
        blob: res.data,
        filename: filenameFromContentDisposition(
          (res.headers as Record<string, string>)["content-disposition"],
        ),
      }
    } catch (err) {
      // A blob request's error body is a Blob; without this every 422
      // (`cut_out_of_range`, `classroom_has_no_calendar`) reads as a 500.
      throw await withJsonErrorBody(err)
    }
  },

  // PRD-66 — the group's materials some enrolled students have no copy of.
  materialGaps: (id: string) =>
    apiClient.get<ClassroomMaterialGaps>(`/classrooms/${id}/material-gaps`).then((r) => r.data),

  // PRD-66 — generate the chosen materials for every student missing them;
  // one result per material (a refusal of one does not undo the others).
  fillMaterialGaps: (id: string, materialIds: string[]) =>
    apiClient
      .post<{ results: MaterialGapFillResult[] }>(`/classrooms/${id}/material-gaps/fill`, {
        material_ids: materialIds,
      })
      .then((r) => r.data.results),

  // PRD-15 §FR-5.3 — bulk dispatch survey path for every dispatchable
  // student in a classroom. Server pre-flights sibling check.
  bulkInviteSurveys: (id: string) =>
    apiClient
      .post<{
        dispatched_count: number
        copied_from_sibling_count: number
        skipped_no_email_count: number
        skipped_already_completed_count: number
      }>(`/classrooms/${id}/survey/bulk-invite`)
      .then((r) => r.data),
}

// PRD-15 §FR-5.3 — Home dashboard "Encuestas pendientes" KPI source.
export const surveyHomeApi = {
  pendingSummary: () =>
    apiClient
      .get<{
        total_pending: number
        by_classroom: { classroom_id: string; name: string; pending: number; total: number }[]
      }>("/home/survey-pending-summary")
      .then((r) => r.data),
}
